import { auth } from './firebase';

const ACTOR_STORAGE_KEY = 'etigo_actor';

// Get Firebase ID token for API authentication
export async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const idToken = await user.getIdToken();
    return idToken;
  } catch {
    return null;
  }
}

export function getActor(): string {
  return localStorage.getItem(ACTOR_STORAGE_KEY) || 'anonymous';
}

export function setActor(actor: string): void {
  localStorage.setItem(ACTOR_STORAGE_KEY, actor);
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function apiClient<T>(
  url: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;

  const headers: HeadersInit = {
    ...fetchOptions.headers,
  };

  if (!skipAuth) {
    const authToken = await getAuthToken();
    if (authToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${authToken}`;
    }
    (headers as Record<string, string>)['x-actor'] = getActor();
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Meta endpoints
export async function fetchMeta(): Promise<{
  operators: { id: string; name: string }[];
  terminals: { id: string; name: string; location_id?: string; keywords?: string[] }[];
  locations: { id: string; name: string }[];
}> {
  return apiClient('/api/meta/operators-terminals-locations');
}

// Direction endpoints
export async function fetchDirections(terminalId: string): Promise<{ steps: import('../types').DirectionStep[] }> {
  return apiClient(`/api/direction/list?terminal_id=${encodeURIComponent(terminalId)}`);
}

export async function upsertDirection(formData: FormData): Promise<{ sql: string; step: import('../types').DirectionStep }> {
  return apiClient('/api/direction/upsert', {
    method: 'POST',
    body: formData,
  });
}

export async function deleteDirection(terminalId: string, sequence: number): Promise<{ success: boolean; sql: string }> {
  return apiClient(`/api/direction/${encodeURIComponent(terminalId)}/${sequence}`, {
    method: 'DELETE',
  });
}

// Route endpoints
export async function fetchRoutes(params: { operator_id?: string; origin_terminal_id?: string } = {}): Promise<{ routes: import('../types').RouteRecord[]; sql?: string }> {
  const searchParams = new URLSearchParams();
  if (params.operator_id) searchParams.set('operator_id', params.operator_id);
  if (params.origin_terminal_id) searchParams.set('origin_terminal_id', params.origin_terminal_id);
  const query = searchParams.toString();
  return apiClient(`/api/route/list${query ? `?${query}` : ''}`);
}

export async function upsertRoute(data: import('../types').RouteUpsertRequest): Promise<{ sql: string; summary: { route_id: string; sub_routes: number; trips: number; waypoints: number } }> {
  return apiClient('/api/route/upsert', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteRoute(id: string): Promise<{ success: boolean; sql: string }> {
  return apiClient(`/api/route/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// Import endpoints
export async function previewImport(formData: FormData): Promise<import('../types').ImportPreviewResponse> {
  return apiClient('/api/import/preview', {
    method: 'POST',
    body: formData,
  });
}

export interface RouteEdit {
  route_name?: string;
  schedule_raw?: string;
  fare_raw?: string;
}

export async function commitImport(data: {
  importId: string;
  mode: 'apply' | 'export_sql';
  conflictStrategy?: 'upsert' | 'fail';
  routeEdits?: Record<number, RouteEdit>; // route index -> edits
}): Promise<import('../types').ImportCommitResponse> {
  return apiClient('/api/import/commit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// Terminal endpoints
export async function upsertTerminal(data: { terminal_id?: string; name: string; location_id?: string; lat?: number; lon?: number; facilities?: string[]; transits?: string[]; keywords?: string[] }): Promise<{ success: boolean; sql: string; terminal: { id: string; name: string; lat?: number; lon?: number } }> {
  return apiClient('/api/terminal/upsert', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateTerminalKeywords(data: { terminal_id: string; keywords: string[] }): Promise<{ success: boolean; sql: string; terminal_id: string; keywords: string[] }> {
  return apiClient('/api/terminal/keywords', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// SQL apply endpoint
export async function applySql(sql: string): Promise<{ success: boolean }> {
  return apiClient('/api/sql/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sql }),
  });
}
